/**
 * LoginTabs - Componente de tabs personalizado para login
 * Basado en el patrón de houseme pero con estilos consistentes
 */

import React from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@shared/ui/shadcn/ui/tabs';
import { cn } from '@shared/lib/utils';

interface LoginTabsProps {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}

export const LoginTabs: React.FC<LoginTabsProps> = ({
  value,
  onValueChange,
  children,
  className,
}) => {
  return (
    <Tabs value={value} onValueChange={onValueChange} className={cn('w-full', className)}>
      {children}
    </Tabs>
  );
};

interface LoginTabsListProps {
  children: React.ReactNode;
  className?: string;
}

export const LoginTabsList: React.FC<LoginTabsListProps> = ({ children, className }) => {
  return (
    <TabsList
      className={cn(
        // ✅ Segmented Control estilo iOS
        'grid w-full grid-cols-3 mb-6 h-11',
        'bg-muted/50 backdrop-blur-sm',
        'border border-border/60',
        'p-1.5 gap-1.5',
        'shadow-sm',
        className
      )}
      style={{
        borderRadius: '0.75rem', // ✅ Bordes más redondeados para look iOS
      }}
    >
      {children}
    </TabsList>
  );
};

interface LoginTabsTriggerProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

export const LoginTabsTrigger: React.FC<LoginTabsTriggerProps> = ({
  value,
  children,
  className,
}) => {
  return (
    <TabsTrigger
      value={value}
      className={cn(
        // ✅ Segmented Control iOS-style
        'inline-flex items-center justify-center',
        'whitespace-nowrap',
        'px-3 py-2 text-xs font-semibold',
        'transition-all duration-300 ease-out',
        // Estado inactivo - Más sutil
        'text-muted-foreground/70',
        'hover:text-foreground/90',
        // Estado activo - Efecto iOS con sombra y elevación
        'data-[state=active]:bg-card',
        'data-[state=active]:text-foreground',
        'data-[state=active]:shadow-md',
        'data-[state=active]:scale-[0.98]',
        'data-[state=active]:font-bold',
        // Focus
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-0',
        // Disabled
        'disabled:pointer-events-none disabled:opacity-50',
        className
      )}
      style={{
        borderRadius: '0.625rem', // ✅ Bordes bien redondeados (10px)
      }}
    >
      {children}
    </TabsTrigger>
  );
};

export { TabsContent as LoginTabsContent };
